import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import { Bell, BellOff, Check, X, Info } from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { useNavigate } from 'react-router-dom';

const NotificationManager = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSChrome, setIsIOSChrome] = useState(false);

  useEffect(() => {
    // Detectar iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const iosChrome = ios && /CriOS/.test(navigator.userAgent);
    setIsIOS(ios);
    setIsIOSChrome(iosChrome);

    if ('Notification' in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPushNotifications = async () => {
    if (!token) {
      showToast.error('Debes iniciar sesión primero');
      return;
    }

    setLoading(true);

    try {
      // Paso 1: Solicitar permiso
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        showToast.error('Permiso denegado para notificaciones');
        setLoading(false);
        return;
      }
      
      // Paso 2: Obtener VAPID key
      const vapidResponse = await fetch(`${API}/notifications/vapid-public-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!vapidResponse.ok) {
        throw new Error('Error obteniendo VAPID key');
      }

      const { publicKey } = await vapidResponse.json();
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      
      // Paso 3: Obtener Service Worker registrado
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      
      // Paso 4: Suscribirse a PushManager (con timeout de 15s)
      const subscriptionPromise = registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT: pushManager.subscribe tardó más de 15 segundos')), 15000)
      );
      
      const subscription = await Promise.race([subscriptionPromise, timeoutPromise]);
      
      // Paso 5: Enviar al backend
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });

      if (!response.ok) {
        throw new Error('Error al guardar suscripción en servidor');
      }

      setIsSubscribed(true);
      showToast.success('¡Notificaciones activadas correctamente!');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      showToast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPushNotifications = async () => {
    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe localmente
        await subscription.unsubscribe();

        // Notificar al backend
        await fetch(`${API}/notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint
          })
        });
      }

      setIsSubscribed(false);
      showToast.success('Notificaciones desactivadas');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      showToast.error('Error al desactivar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const isNative = window.Capacitor?.isNative || navigator.userAgent.includes('Capacitor');

  if (!token || !user) {
    return null;
  }

  // 1. CASO NATIVO (Capacitor Android/iOS)
  if (isNative) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-6 border-2 border-blue-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-900">
            <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Notificaciones en la App
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              En la aplicación nativa, las notificaciones se gestionan directamente desde los ajustes de tu dispositivo.
            </p>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                ✅ <strong>Configuración:</strong> Ve a Ajustes &gt; Aplicaciones &gt; PGastos &gt; Notificaciones para activar o desactivar las alertas.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. CASO iOS SAFARI (No instalado)
  const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  if (isIOS && !isIOSChrome && !isStandalone) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-900">
            <Info className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Notificaciones Push en iPhone
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Safari en iPhone requiere que instales la aplicación como PWA para habilitar las notificaciones push.
            </p>

            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                📱 Cómo instalar:
              </p>
              <ol className="text-sm text-blue-800 dark:text-blue-300 list-decimal list-inside space-y-1">
                <li>Presiona el botón de compartir en Safari</li>
                <li>Selecciona "Agregar a pantalla de inicio"</li>
                <li>Abre la app desde tu inicio y activa las notificaciones aquí mismo.</li>
              </ol>
            </div>

            <button
              onClick={() => navigate('/install-pwa')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Info className="w-4 h-4" />
              Ver guía detallada
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. CASO WEB / PWA (Navegador compatible)
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isSubscribed ? 'bg-green-100 dark:bg-green-900' : 'bg-slate-100 dark:bg-slate-700'
        }`}>
          {isSubscribed ? (
            <Bell className="w-6 h-6 text-green-600 dark:text-green-400" />
          ) : (
            <BellOff className="w-6 h-6 text-slate-600 dark:text-slate-400" />
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Notificaciones Push
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {isSubscribed 
              ? 'Recibirás alertas sobre tus finanzas en este dispositivo.'
              : 'Activa las notificaciones para recibir recordatorios importantes.'
            }
          </p>

          {permission === 'denied' && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                Has bloqueado las notificaciones. Cámbialo en los ajustes de tu navegador para poder activarlas.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            {isSubscribed ? (
              <button
                onClick={unsubscribeFromPushNotifications}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                <X className="w-4 h-4" />
                {loading ? 'Desactivando...' : 'Desactivar'}
              </button>
            ) : (
              <button
                onClick={subscribeToPushNotifications}
                disabled={loading || permission === 'denied'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                <Check className="w-4 h-4" />
                {loading ? 'Activando...' : 'Activar Notificaciones'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;
